import { useEffect, useMemo } from 'react';
import { Alert, Form, Input, Modal, Select, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import type { ReportDefinition } from '@goelectric/shared-types';
import {
  defaultEmailSubject,
  MAX_ASUNTO_LENGTH,
  MAX_DESTINATARIOS,
  normalizeDestinatarios,
  type FileReportFormat,
  type ReportEmailValues,
  validateDestinatarios,
} from '../utils/email';

const FORMAT_LABELS: Record<FileReportFormat, string> = {
  csv: 'CSV',
  xlsx: 'Excel',
  pdf: 'PDF',
};

export interface ReportEmailModalProps {
  open: boolean;
  /** Definición del reporte: títulos y formatos de archivo soportados. */
  definition: ReportDefinition | null;
  /** Pre-carga del reenvío; sin ella arranca vacío con el asunto automático. */
  initial?: {
    destinatarios?: string[];
    formato?: string;
    asunto?: string | null;
  } | null;
  sending?: boolean;
  /** Error del envío; el modal queda abierto para corregir y reintentar. */
  error?: string | null;
  onSend: (values: ReportEmailValues) => void;
  onClose: () => void;
}

/**
 * Modal "Enviar por correo". Los formatos salen de la definición (nunca
 * `json`), el asunto viene pre-cargado con el automático y es editable.
 */
export function ReportEmailModal({
  open,
  definition,
  initial,
  sending = false,
  error = null,
  onSend,
  onClose,
}: ReportEmailModalProps) {
  const [form] = Form.useForm<ReportEmailValues>();

  const formatos = useMemo<FileReportFormat[]>(() => {
    const declarados = (definition?.formatos ?? []).filter(
      (formato): formato is FileReportFormat => formato !== 'json',
    );
    return declarados;
  }, [definition]);

  // Cada apertura rearma el formulario: reenvío con sus datos, envío nuevo en blanco.
  useEffect(() => {
    if (!open || !definition) return;
    const inicial = initial ?? {};
    const formatoInicial =
      formatos.find((formato) => formato === inicial.formato) ?? formatos[0] ?? 'csv';
    form.setFieldsValue({
      destinatarios: inicial.destinatarios ?? [],
      formato: formatoInicial,
      asunto: inicial.asunto ?? defaultEmailSubject(definition.titulo),
    });
  }, [open, definition, initial, formatos, form]);

  const handleOk = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    onSend({
      destinatarios: normalizeDestinatarios(values.destinatarios),
      formato: values.formato,
      asunto: values.asunto?.trim() ?? '',
    });
  };

  return (
    <Modal
      open={open}
      title="Enviar por correo"
      okText="Enviar"
      cancelText="Cancelar"
      confirmLoading={sending}
      onOk={() => void handleOk()}
      onCancel={onClose}
      forceRender
    >
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      )}

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="destinatarios"
          label="Destinatarios"
          extra={`Hasta ${MAX_DESTINATARIOS} correos.`}
          rules={[
            {
              validator: async (_rule, value: string[] | undefined) => {
                const message = validateDestinatarios(value);
                if (message) throw new Error(message);
              },
            },
          ]}
        >
          <Select
            mode="tags"
            tokenSeparators={[',', ';', ' ']}
            placeholder="nombre@dominio.com"
            notFoundContent={null}
            suffixIcon={<MailOutlined />}
          />
        </Form.Item>

        <Form.Item
          name="formato"
          label="Formato"
          rules={[{ required: true, message: 'Elegí un formato.' }]}
        >
          <Select
            options={formatos.map((formato) => ({
              value: formato,
              label: FORMAT_LABELS[formato],
            }))}
          />
        </Form.Item>

        <Form.Item
          name="asunto"
          label="Asunto"
          extra="Si lo dejás vacío se usa el asunto automático."
          rules={[
            {
              max: MAX_ASUNTO_LENGTH,
              message: `El asunto no puede superar los ${MAX_ASUNTO_LENGTH} caracteres.`,
            },
          ]}
        >
          <Input placeholder={definition ? defaultEmailSubject(definition.titulo) : 'Asunto'} />
        </Form.Item>
      </Form>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        El correo incluye un enlace de descarga con vencimiento.
      </Typography.Text>
    </Modal>
  );
}
